import {
  AddAccessTokenToIndexedDB,
  AddAccountToIndexedDB,
  AddAssetTemplateToIndexedDB,
  AddBankAccountToIndexedDB,
  AddContactStatementRemarkFrequencyToIndexedDB,
  AddPackageToIndexedDB,
  AddProductToIndexedDB,
  AddSelectedMerchantToIndexedDB,
  AddSettingProductPriceToIndexedDB,
  AddSettingPurchaseOrderPartialToIndexedDB,
  AddSettingQuotationPartialToIndexedDB,
  AddSettingTableToIndexedDB,
  AddSettingViewTransactionToIndexedDB,
  AddUserInAppToIndexedDB,
  AddUserProfileToIndexedDB,
  ClearAccessTokenFromIndexedDB,
  ClearAccountFromIndexedDB,
  ClearAllCacheFromIndexedDB,
  ClearAssetTemplateFromIndexedDB,
  ClearBankAccountFromIndexedDB,
  ClearContactStatementRemarkFrequencyFromIndexedDB,
  ClearPackageFromIndexedDB,
  ClearProductFromIndexedDB,
  ClearSelectedMerchantFromIndexedDB,
  ClearSettingTableFromIndexedDB,
  ClearSettingViewTransactionFromIndexedDB,
  ClearUserInAppFromIndexedDB,
  ClearUserProfileFromIndexedDB,
  DeleteAccountFromIndexedDB,
  DeleteAssetTemplateFromIndexedDB,
  DeleteBankAccountFromIndexedDB,
  DeleteContactStatementRemarkFrequencyFromIndexedDB,
  DeleteMultipleSettingViewTransactionFromIndexDB,
  DeletePackageFromIndexedDB,
  DeleteProductFromIndexedDB,
  DeleteSettingProductPriceFromIndexedDB,
  DeleteSettingPurchaseOrderPartialFromIndexedDB,
  DeleteSettingQuotationPartialFromIndexedDB,
  DeleteSettingTableFromIndexedDB,
  DeleteSettingViewTransactionFromIndexedDB,
  DeleteUserInAppFromIndexedDB,
  GetAccessTokenFromIndexedDB,
  GetAccountFromIndexedDB,
  GetAssetTemplateFromIndexedDB,
  GetBankAccountFromIndexedDB,
  GetContactStatementRemarkFrequencyFromIndexedDB,
  GetPackageFromIndexedDB,
  GetProductFromIndexedDB,
  GetSelectedMerchantFromIndexedDB,
  GetSettingProductPriceFromIndexedDB,
  GetSettingPurchaseOrderPartialFromIndexedDB,
  GetSettingQuotationPartialFromIndexedDB,
  GetSettingTableFromIndexedDB,
  GetSettingViewTransactionFromIndexedDB,
  GetUserInAppFromIndexedDB,
  GetUserProfileFromIndexedDB,
} from "@/IndexedDB/CacheIndexedDB";
import Enums from "@/enums/enums";
import userInfo from "@/function/userInfo";

export const CheckAccessTokenCacheValid = async () => {
  let accessTokenCacheValid = true;
  let accessTokenCache = await GetAccessTokenFromCache();
  const accessToken = userInfo.GetAccessToken();
  if (!accessTokenCache) {
    await AddAccessTokenToIndexedDB(accessToken);
    accessTokenCache = accessToken;
  }

  if (accessTokenCache !== accessToken) {
    accessTokenCacheValid = false;
  }

  if (!accessTokenCacheValid) {
    await ClearAllCacheFromIndexedDB();
  }

  return accessTokenCacheValid;
};

//#region AccessToken
export const GetAccessTokenFromCache = async () => {
  const accessToken = await GetAccessTokenFromIndexedDB();
  if (accessToken) {
    return accessToken;
  }

  return null;
};

export const AddAccessTokenCache = async (accessToken) => {
  await AddAccessTokenToIndexedDB(accessToken);
};

export const ClearAccessTokenCache = async () => {
  await ClearAccessTokenFromIndexedDB();
};
//#endregion

//#region UserProfile
export const GetUserProfileFromCache = async () => {
  if (await CheckAccessTokenCacheValid()) {
    const userProfile = await GetUserProfileFromIndexedDB();
    if (userProfile && userProfile.userId > 0) {
      return userProfile;
    }
  }
  return null;
};

export const AddUserProfileCache = async (userProfile) => {
  await AddUserProfileToIndexedDB(userProfile);
};

export const ClearUserProfileCache = async () => {
  await ClearUserProfileFromIndexedDB();
};
//#endregion

//#region SelectedMerchant
export const GetSelectedMerchantFromCache = async (merchantId) => {
  if (await CheckAccessTokenCacheValid()) {
    const selectedMerchant = await GetSelectedMerchantFromIndexedDB(merchantId);
    if (selectedMerchant && selectedMerchant.merchantId > 0) {
      return selectedMerchant;
    }
  }
  return null;
};

export const AddSelectedMerchantCache = async (selectedMerchant) => {
  await AddSelectedMerchantToIndexedDB(selectedMerchant);
};

export const ClearSelectedMerchantCache = async () => {
  await ClearSelectedMerchantFromIndexedDB();
};
//#endregion

//#region SettingTable
function GetSettingTableIndexedDBKey(merchantId, documentType) {
  const mapper = {
    [Enums.SettingTableDocumentType.Quotation]: "quotation",
    [Enums.SettingTableDocumentType.DepositReceipt]: "deposit_receipt",
    [Enums.SettingTableDocumentType.Invoice]: "invoice",
    [Enums.SettingTableDocumentType.Receipt]: "receipt",
    [Enums.SettingTableDocumentType.SalesTaxInvoice]: "sales_tax_invoice",
    [Enums.SettingTableDocumentType.CreditNote]: "credit_note",
    [Enums.SettingTableDocumentType.DebitNote]: "debit_note",
    [Enums.SettingTableDocumentType.BillingNote]: "billing_note",
    [Enums.SettingTableDocumentType.PurchaseOrder]: "purchase_order",
    [Enums.SettingTableDocumentType.DepositPayment]: "deposit_payment",
    [Enums.SettingTableDocumentType.GoodsReceipt]: "goods_receipt",
    [Enums.SettingTableDocumentType.InvoiceReceipt]: "invoice_receipt",
    [Enums.SettingTableDocumentType.PurchaseInventoryRecord]: "purchase_inventory_record",
    [Enums.SettingTableDocumentType.ExpenseRecord]: "expense_record",
    [Enums.SettingTableDocumentType.PurchaseOrderAsset]: "purchase_order_asset",
    [Enums.SettingTableDocumentType.PurchaseAsset]: "purchase_asset",
    [Enums.SettingTableDocumentType.PurchaseTaxInvoice]: "purchase_tax_invoice",
    [Enums.SettingTableDocumentType.ReceiveCreditNote]: "receive_credit_note",
    [Enums.SettingTableDocumentType.ReceiveDebitNote]: "receive_debit_note",
    [Enums.SettingTableDocumentType.CombinedPaymentNote]: "combined_payment_note",
    [Enums.SettingTableDocumentType.DailyJournal]: "daily_journal",
    [Enums.SettingTableDocumentType.PurchaseRequisition]: "purchase_requisition",
  };

  return `setting_table:${mapper[documentType]}:merchant_${merchantId}`;
}

export const GetSettingTableFromCache = async (merchantId, documentType = Enums.SettingTableDocumentType.None) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetSettingTableIndexedDBKey(merchantId, documentType);
    const settingTable = await GetSettingTableFromIndexedDB(key);

    if (settingTable) {
      return settingTable;
    }
  }

  return null;
};

export const AddSettingTableCache = async (
  merchantId,
  documentType = Enums.SettingTableDocumentType.None,
  settingTable
) => {
  const key = GetSettingTableIndexedDBKey(merchantId, documentType);
  await AddSettingTableToIndexedDB(key, settingTable);
};

export const DeleteSettingTableCache = async (merchantId, documentType = Enums.SettingTableDocumentType.None) => {
  const key = GetSettingTableIndexedDBKey(merchantId, documentType);
  await DeleteSettingTableFromIndexedDB(key);
};

export const ClearSettingTableCache = async () => {
  await ClearSettingTableFromIndexedDB();
};
//#endregion

//#region SettingViewTransaction
function GetSettingViewTransactionIndexedDBKey(merchantId, documentType) {
  const mapper = {
    [Enums.SettingViewTransactionDocumentType.Quotation]: "quotation",
    [Enums.SettingViewTransactionDocumentType.DepositReceipt]: "deposit_receipt",
    [Enums.SettingViewTransactionDocumentType.Invoice]: "invoice",
    [Enums.SettingViewTransactionDocumentType.InvoiceSellAsset]: "invoice_sell_asset",
    [Enums.SettingViewTransactionDocumentType.RecurringInvoice]: "recurring_invoice",
    [Enums.SettingViewTransactionDocumentType.Receipt]: "receipt",
    [Enums.SettingViewTransactionDocumentType.ReceiptSellAsset]: "receipt_sell_asset",
    [Enums.SettingViewTransactionDocumentType.RecurringReceipt]: "recurring_receipt",
    [Enums.SettingViewTransactionDocumentType.SalesTaxInvoice]: "sales_tax_invoice",
    [Enums.SettingViewTransactionDocumentType.CreditNote]: "credit_note",
    [Enums.SettingViewTransactionDocumentType.DebitNote]: "debit_note",
    [Enums.SettingViewTransactionDocumentType.PurchaseOrder]: "purchase_order",
    [Enums.SettingViewTransactionDocumentType.GoodsReceipt]: "goods_receipt",
    [Enums.SettingViewTransactionDocumentType.DepositPayment]: "deposit_payment",
    [Enums.SettingViewTransactionDocumentType.InvoiceReceipt]: "invoice_receipt",
    [Enums.SettingViewTransactionDocumentType.PurchaseInventoryRecord]: "purchase_inventory_record",
    [Enums.SettingViewTransactionDocumentType.ExpenseRecord]: "expense_record",
    [Enums.SettingViewTransactionDocumentType.RecurringExpense]: "recurring_expense",
    [Enums.SettingViewTransactionDocumentType.PurchaseAsset]: "purchase_asset",
    [Enums.SettingViewTransactionDocumentType.PurchaseOrderAsset]: "purchase_order_asset",
    [Enums.SettingViewTransactionDocumentType.ReceiveCreditNote]: "receive_credit_note",
    [Enums.SettingViewTransactionDocumentType.ReceiveDebitNote]: "receive_debit_note",
    [Enums.SettingViewTransactionDocumentType.PurchaseRequisition]: "purchase_requisition",
  };

  return `setting_view_transaction:${mapper[documentType]}:merchant_${merchantId}`;
}

export const GetSettingViewTransactionFromCache = async (
  merchantId,
  documentType = Enums.SettingViewTransactionDocumentType.None
) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetSettingViewTransactionIndexedDBKey(merchantId, documentType);
    const settingViewTransaction = await GetSettingViewTransactionFromIndexedDB(key);

    if (settingViewTransaction) {
      return settingViewTransaction;
    }
  }

  return null;
};

export const AddSettingViewTransactionCache = async (
  merchantId,
  documentType = Enums.SettingViewTransactionDocumentType.None,
  settingViewTransaction
) => {
  const key = GetSettingViewTransactionIndexedDBKey(merchantId, documentType);
  await AddSettingViewTransactionToIndexedDB(key, settingViewTransaction);
};

export const DeleteSettingViewTransactionCache = async (
  merchantId,
  documentType = Enums.SettingViewTransactionDocumentType.None
) => {
  const key = GetSettingViewTransactionIndexedDBKey(merchantId, documentType);
  await DeleteSettingViewTransactionFromIndexedDB(key);
};

export const DeleteSettingViewTransactionsByMerchantCache = async (merchantId) => {
  let keyList = [
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.Quotation),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.DepositReceipt),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.Invoice),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.InvoiceSellAsset),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.RecurringInvoice),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.Receipt),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.ReceiptSellAsset),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.RecurringReceipt),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.SalesTaxInvoice),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.CreditNote),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.DebitNote),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.PurchaseRequisition),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.PurchaseOrder),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.GoodsReceipt),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.DepositPayment),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.InvoiceReceipt),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.PurchaseInventoryRecord),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.ExpenseRecord),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.RecurringExpense),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.PurchaseAsset),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.PurchaseOrderAsset),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.ReceiveCreditNote),
    GetSettingViewTransactionIndexedDBKey(merchantId, Enums.SettingViewTransactionDocumentType.ReceiveDebitNote),
  ];

  await DeleteMultipleSettingViewTransactionFromIndexDB(keyList);
};

export const ClearSettingViewTransactionCache = async () => {
  await ClearSettingViewTransactionFromIndexedDB();
};
//#endregion

//#region SettingProductPrice
function GetSettingProductPriceIndexedDBKey(merchantId, productTemplateId) {
  return `merchant_${merchantId}:productTemplate_${productTemplateId}`;
}

export const GetSettingProductPriceFromCache = async (merchantId, productTemplateId) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetSettingProductPriceIndexedDBKey(merchantId, productTemplateId);
    const settingProductPrice = await GetSettingProductPriceFromIndexedDB(key);

    if (settingProductPrice) {
      return settingProductPrice;
    }
  }

  return null;
};
export const AddSettingProductPriceCache = async (merchantId, productTemplateId, settingProductPrice) => {
  const key = GetSettingProductPriceIndexedDBKey(merchantId, productTemplateId);
  await AddSettingProductPriceToIndexedDB(key, settingProductPrice);
};

export const DeleteSettingProductPriceCache = async (merchantId, productTemplateId) => {
  const key = GetSettingProductPriceIndexedDBKey(merchantId, productTemplateId);
  await DeleteSettingProductPriceFromIndexedDB(key);
};

//#endregion

// #region Account
function GetAccountListIndexedDBKey(merchantId) {
  return `account_list:merchant_${merchantId}`;
}

export const GetAccountListFromCache = async (merchantId) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetAccountListIndexedDBKey(merchantId);
    const accountList = await GetAccountFromIndexedDB(key);

    if (accountList) {
      return accountList;
    }
  }

  return null;
};

export const AddAccountListCache = async (merchantId, accountList) => {
  const key = GetAccountListIndexedDBKey(merchantId);
  await AddAccountToIndexedDB(key, accountList);
};

export const DeleteAccountListCache = async (merchantId) => {
  const key = GetAccountListIndexedDBKey(merchantId);
  await DeleteAccountFromIndexedDB(key);
};

export const ClearAccountCache = async () => {
  await ClearAccountFromIndexedDB();
};
// #endregion

// #region UserInApp
function GetUserInAppPEAKIndexedDBKey(merchantId) {
  return `user_in_app_peak:merchant_${merchantId}`;
}

export const GetUserInAppPEAKFromCache = async (merchantId) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetUserInAppPEAKIndexedDBKey(merchantId);
    const userInAppPEAK = await GetUserInAppFromIndexedDB(key);

    if (userInAppPEAK) {
      return userInAppPEAK;
    }
  }

  return null;
};

export const AddUserInAppPEAKCache = async (merchantId, userInAppPEAK) => {
  const key = GetUserInAppPEAKIndexedDBKey(merchantId);
  await AddUserInAppToIndexedDB(key, userInAppPEAK);
};

export const DeleteUserInAppPEAKCache = async (merchantId) => {
  const key = GetUserInAppPEAKIndexedDBKey(merchantId);
  await DeleteUserInAppFromIndexedDB(key);
};

export const ClearUserInAppPEAKCache = async () => {
  await ClearUserInAppFromIndexedDB();
};
// #endregion

// #region Package
function GetPackageIndexedDBKey(merchantId) {
  return `package:merchant_${merchantId}`;
}

export const GetPackageFromCache = async (merchantId) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetPackageIndexedDBKey(merchantId);
    const _package = await GetPackageFromIndexedDB(key);

    if (_package) {
      return _package;
    }
  }

  return null;
};

export const AddPackageCache = async (merchantId, _package) => {
  const key = GetPackageIndexedDBKey(merchantId);
  await AddPackageToIndexedDB(key, _package);
};

export const DeletePackageCache = async (merchantId) => {
  const key = GetPackageIndexedDBKey(merchantId);
  await DeletePackageFromIndexedDB(key);
};

export const ClearPackageCache = async () => {
  await ClearPackageFromIndexedDB();
};
// #endregion

// #region GetSettingQuotationPartial
export const GetSettingQuotationPartialFromCache = async (merchantId) => {
  if (await CheckAccessTokenCacheValid()) {
    return await GetSettingQuotationPartialFromIndexedDB(merchantId);
  }

  return null;
};

export const AddSettingQuotationPartialCache = async (merchantId, settingQuotationPartial) => {
  await AddSettingQuotationPartialToIndexedDB(merchantId, settingQuotationPartial);
};

export const DeleteSettingQuotationPartialCache = async (merchantId) => {
  await DeleteSettingQuotationPartialFromIndexedDB(merchantId);
};
// #endregion

// #region GetSettingPurchaseOrderPartial
export const GetSettingPurchaseOrderPartialFromCache = async (merchantId) => {
  if (await CheckAccessTokenCacheValid()) {
    return await GetSettingPurchaseOrderPartialFromIndexedDB(merchantId);
  }

  return null;
};

export const AddSettingPurchaseOrderPartialCache = async (merchantId, settingPurchaseOrderPartial) => {
  await AddSettingPurchaseOrderPartialToIndexedDB(merchantId, settingPurchaseOrderPartial);
};

export const DeleteSettingPurchaseOrderPartialCache = async (merchantId) => {
  await DeleteSettingPurchaseOrderPartialFromIndexedDB(merchantId);
};
// #endregion

// #region BankAccount
function GetBankAccountListIndexedDBKey(merchantId) {
  return `bankAccount_list:merchant_${merchantId}`;
}
export const GetBankAccountListFromCache = async (merchantId) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetBankAccountListIndexedDBKey(merchantId);
    return await GetBankAccountFromIndexedDB(key);
  }

  return null;
};
export const AddBankAccountListCache = async (merchantId, bankAccountList) => {
  const key = GetBankAccountListIndexedDBKey(merchantId);
  await AddBankAccountToIndexedDB(key, bankAccountList);
};

export const DeleteBankAccountListCache = async (merchantId) => {
  const key = GetBankAccountListIndexedDBKey(merchantId);
  await DeleteBankAccountFromIndexedDB(key);
};

export const ClearBankAccountCache = async () => {
  await ClearBankAccountFromIndexedDB();
};
// #endregion

//#region Product
function GetProductStatusIndexedDBKey(merchantId) {
  return `product_status:merchant_${merchantId}`;
}

function GetProductListIndexedDBKey(merchantId) {
  return `product_list:merchant_${merchantId}`;
}

export const GetProductStatusFromCache = async (merchantId) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetProductStatusIndexedDBKey(merchantId);
    return await GetProductFromIndexedDB(key);
  }

  return null;
};

export const GetProductListFromCache = async (merchantId) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetProductListIndexedDBKey(merchantId);
    return await GetProductFromIndexedDB(key);
  }

  return null;
};

export const GetProductListMapFromCache = async (merchantId) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetProductListIndexedDBKey(merchantId);
    const productList = await GetProductFromIndexedDB(key);
    if (productList && productList.length > 0) {
      const productMap = new Map();
      for (const product of productList) {
        productMap.set(product.id, product);
      }
      return productMap;
    }
    return null;
  }

  return null;
};

export const GetProductListMapCodeFromCache = async (merchantId) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetProductListIndexedDBKey(merchantId);
    const productList = await GetProductFromIndexedDB(key);
    if (productList && productList.length > 0) {
      const productMap = new Map();
      for (const product of productList) {
        productMap.set(product.code, product);
      }
      return productMap;
    }
    return null;
  }

  return null;
};

export const AddProductListCache = async (merchantId, productList) => {
  const key = GetProductListIndexedDBKey(merchantId);
  await AddProductToIndexedDB(key, productList);
};

export const AddProductStatusCache = async (merchantId, productStatus) => {
  const key = GetProductStatusIndexedDBKey(merchantId);
  await AddProductToIndexedDB(key, productStatus);
};

export const DeleteProductListCache = async (merchantId) => {
  const keyProductionList = GetProductListIndexedDBKey(merchantId);
  const keyProductionStatus = GetProductStatusIndexedDBKey(merchantId);
  await DeleteProductFromIndexedDB(keyProductionList);
  await DeleteProductFromIndexedDB(keyProductionStatus);
};

export const ClearProductCache = async () => {
  await ClearProductFromIndexedDB();
};
//#endregion

//#region ContactStatementRemarkFrequency
function GetContactStatementRemarkFrequencyStatusIndexedDBKey(merchantId, bankReferenceBankType) {
  return `contactStatementRemarkFrequency_status:merchant_${merchantId}_bankReferenceBankType_${bankReferenceBankType}`;
}

function GetContactStatementRemarkFrequencyIndexedDBKey(merchantId, bankReferenceBankType) {
  return `contactStatementRemarkFrequency_list:merchant_${merchantId}_bankReferenceBankType_${bankReferenceBankType}`;
}

export const GetContactStatementRemarkFrequencyStatusFromCache = async (merchantId, bankReferenceBankType) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetContactStatementRemarkFrequencyStatusIndexedDBKey(merchantId, bankReferenceBankType);
    return await GetContactStatementRemarkFrequencyFromIndexedDB(key);
  }

  return null;
};

export const GetContactStatementRemarkFrequencyListFromCache = async (merchantId, bankReferenceBankType) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetContactStatementRemarkFrequencyIndexedDBKey(merchantId, bankReferenceBankType);
    return await GetContactStatementRemarkFrequencyFromIndexedDB(key);
  }

  return null;
};

export const GetContactStatementRemarkFrequencyMapFromCache = async (merchantId, bankReferenceBankType) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetContactStatementRemarkFrequencyIndexedDBKey(merchantId, bankReferenceBankType);
    const contactStatementRemarkFrequencyListFromCache = await GetContactStatementRemarkFrequencyFromIndexedDB(key);
    if (contactStatementRemarkFrequencyListFromCache && contactStatementRemarkFrequencyListFromCache.length > 0) {
      const contactStatementRemarkFrequencyMap = new Map();
      for (const contactStatementRemarkFrequencyFromCache of contactStatementRemarkFrequencyListFromCache) {
        if (contactStatementRemarkFrequencyMap.has(contactStatementRemarkFrequencyFromCache.statementRemark)) {
          const contactStatementRemarkFrequency = contactStatementRemarkFrequencyMap.get(
            contactStatementRemarkFrequencyFromCache.statementRemark
          );
          if (
            !contactStatementRemarkFrequency ||
            (contactStatementRemarkFrequency &&
              contactStatementRemarkFrequency.count < contactStatementRemarkFrequencyFromCache.count)
          ) {
            contactStatementRemarkFrequencyMap.set(
              contactStatementRemarkFrequencyFromCache.statementRemark,
              contactStatementRemarkFrequencyFromCache
            );
          }
        } else {
          contactStatementRemarkFrequencyMap.set(
            contactStatementRemarkFrequencyFromCache.statementRemark,
            contactStatementRemarkFrequencyFromCache
          );
        }
      }
      return contactStatementRemarkFrequencyMap;
    }
    return null;
  }

  return null;
};

export const AddContactStatementRemarkFrequencyListCache = async (
  merchantId,
  bankReferenceBankType,
  contactStatementRemarkFrequencyList
) => {
  const key = GetContactStatementRemarkFrequencyIndexedDBKey(merchantId, bankReferenceBankType);
  await AddContactStatementRemarkFrequencyToIndexedDB(key, contactStatementRemarkFrequencyList);
};

export const AddContactStatementRemarkFrequencyStatusCache = async (
  merchantId,
  bankReferenceBankType,
  contactStatementRemarkFrequencyStatus
) => {
  const key = GetContactStatementRemarkFrequencyStatusIndexedDBKey(merchantId, bankReferenceBankType);
  await AddContactStatementRemarkFrequencyToIndexedDB(key, contactStatementRemarkFrequencyStatus);
};

export const DeleteContactStatementRemarkFrequencyListCache = async (merchantId, bankReferenceBankType) => {
  const key = GetContactStatementRemarkFrequencyListIndexedDBKey(merchantId, bankReferenceBankType);
  await DeleteContactStatementRemarkFrequencyFromIndexedDB(key);
};

export const ClearContactStatementRemarkFrequencyCache = async () => {
  await ClearContactStatementRemarkFrequencyFromIndexedDB();
};
//#endregion

//#region AssetTemplate
function GetAssetTemplateStatusIndexedDBKey(merchantId) {
  return `assetTemplate_status:merchant_${merchantId}`;
}

function GetAssetTemplateListIndexedDBKey(merchantId) {
  return `assetTemplate_list:merchant_${merchantId}`;
}

export const GetAssetTemplateStatusFromCache = async (merchantId) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetAssetTemplateStatusIndexedDBKey(merchantId);
    return await GetAssetTemplateFromIndexedDB(key);
  }

  return null;
};

export const GetAssetTemplateListFromCache = async (merchantId) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetAssetTemplateListIndexedDBKey(merchantId);
    return await GetAssetTemplateFromIndexedDB(key);
  }

  return null;
};

export const GetAssetTemplateListMapFromCache = async (merchantId) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetAssetTemplateListIndexedDBKey(merchantId);
    const assetTemplateList = await GetAssetTemplateFromIndexedDB(key);
    if (assetTemplateList && assetTemplateList.length > 0) {
      const assetTemplateListMap = new Map();
      for (const assetTemplate of assetTemplateList) {
        assetTemplateListMap.set(assetTemplate.id, assetTemplate);
      }
      return assetTemplateListMap;
    }
    return null;
  }

  return null;
};

export const GetAssetTemplateListMapCodeFromCache = async (merchantId) => {
  if (await CheckAccessTokenCacheValid()) {
    const key = GetAssetTemplateListIndexedDBKey(merchantId);
    const assetTemplateList = await GetAssetTemplateFromIndexedDB(key);
    if (assetTemplateList && assetTemplateList.length > 0) {
      const assetTemplateMap = new Map();
      for (const assetTemplate of assetTemplateList) {
        assetTemplateMap.set(assetTemplate.code, assetTemplate);
      }
      return assetTemplateMap;
    }
    return null;
  }

  return null;
};

export const AddAssetTemplateListCache = async (merchantId, assetTemplateList) => {
  const key = GetAssetTemplateListIndexedDBKey(merchantId);
  await AddAssetTemplateToIndexedDB(key, assetTemplateList);
};

export const AddAssetTemplateStatusCache = async (merchantId, assetTemplateStatus) => {
  const key = GetAssetTemplateStatusIndexedDBKey(merchantId);
  await AddAssetTemplateToIndexedDB(key, assetTemplateStatus);
};

export const DeleteAssetTemplateListCache = async (merchantId) => {
  const keyAssetTemplateList = GetAssetTemplateListIndexedDBKey(merchantId);
  const keyAssetTemplateStatus = GetAssetTemplateStatusIndexedDBKey(merchantId);
  await DeleteAssetTemplateFromIndexedDB(keyAssetTemplateList);
  await DeleteAssetTemplateFromIndexedDB(keyAssetTemplateStatus);
};

export const ClearAssetTemplateCache = async () => {
  await ClearAssetTemplateFromIndexedDB();
};
//#endregion
